/**
 * useInboxInfinite Hook
 *
 * TanStack useInfiniteQuery for inbox emails with infinite scroll.
 * First page: 25 emails; each next page loads 25 more.
 */
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { postInboxFetch } from '@/api/generated/inbox/inbox';
import { queryKeys } from '@/config/query/queryKeys';

const INBOX_PAGE_SIZE = 25;

interface UseInboxInfiniteOptions {
  contextEmailIds?: string[] | undefined;
  enabled?: boolean;
  search?: string;
  sessionId?: string | null | undefined;
}

export function useInboxInfinite({
  contextEmailIds,
  enabled = true,
  search,
  sessionId,
}: UseInboxInfiniteOptions = {}) {
  const query = useInfiniteQuery({
    enabled,
    getNextPageParam: (lastPage, allPages) => {
      const hasNextLink = Boolean(lastPage['@odata.nextLink']);
      return hasNextLink ? allPages.length : undefined;
    },
    initialPageParam: 0,
    // Keep the previous result set rendered while a new search key fetches —
    // otherwise every debounced keystroke unmounts the list (isLoading flips
    // true) and replays the staggered entrance animation from scratch.
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const skip = pageParam * INBOX_PAGE_SIZE;
      return postInboxFetch({
        limit: INBOX_PAGE_SIZE,
        skip,
        ...(search?.trim() ? { search: search.trim() } : {}),
        ...(contextEmailIds?.length ? { contextEmailIds } : {}),
      });
    },
    queryKey: [
      ...queryKeys.inbox.fetch(INBOX_PAGE_SIZE, search, contextEmailIds, sessionId),
      'infinite',
    ],
    staleTime: 5 * 60 * 1000,
  });

  return query;
}
